import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import { useClickAway, useUpdate } from 'ahooks';
import { notification, Icon } from 'tea-component';
import { useIntl } from 'umi';
import CommonBubble from '@home/components/CommonBubble';
import EllipsisHover from '@gp/components/EllipsisHover';
import { useDraggableList } from './hooks/useDraggableList';
import { useConfirmDelete } from './hooks/useConfirmDelete';
import useSlideEffect from './hooks/useSlideEffect';
import {
  generateCopyName,
  generateNewSheetName,
  validateSheetName,
  getUniqueSheetName
} from './utils';
import './index.scss';
// 导入图标资源
import iconCopy from '@/pages/aigc/assets/svg/icon-copy-new.svg';
import iconEdit from '@/pages/aigc/assets/svg/agent-ai-rename.svg';
import iconDelete from '@/pages/aigc/assets/svg/icon-delete.svg';
import iconSheetAdd from '@/pages/aigc/assets/svg/agent-sheet-add.svg';
import iconSheetMore from '@/pages/aigc/assets/svg/agent-sheet-more.svg';

/**
 * 工作表数据结构
 */
export interface ISheet {
  /** 工作表唯一标识 */
  id: string;
  /** 工作表名称 */
  name: string;
  /** 副本来源 sheet id（仅创建副本时存在，父组件处理后应清除） */
  sourceId?: string;
}

/**
 * 右键菜单项配置
 */
export interface IContextMenuItem {
  /** 菜单项唯一标识 */
  key: string;
  /** 菜单项显示文本 */
  label: string;
  /** 图标类型（tea-component Icon type）- 已弃用 */
  icon?: string;
  /** 图标图片地址 */
  iconSrc?: string;
  /** 是否在顶部显示分隔线 */
  divider?: boolean;
  /** 是否禁用 */
  disabled?: boolean;
  /** 点击回调 */
  onClick?: (sheetId: string) => void;
}

/**
 * ExcelSheetTabs 组件属性
 */
interface IExcelSheetTabsProps {
  /** 工作表列表 */
  sheets: ISheet[];
  /** 当前激活的工作表ID */
  activeSheetId: string;
  /** 切换工作表时的回调 */
  onChange?: (sheetId: string) => void;
  /** 工作表列表变化时的回调（新增、删除、重命名、排序） */
  onSheetsChange?: (sheets: ISheet[]) => void;
  /** 自定义类名 */
  className?: string;
  /** 自定义右键菜单项，不传则使用默认菜单 */
  contextMenuItems?: IContextMenuItem[];
  /** 是否为只读模式（分享场景下为 true，禁用新增/删除/重命名/拖拽等编辑操作） */
  readonly?: boolean;
}

/** 滚动按钮宽度 */
const SCROLL_BUTTONS_WIDTH = 80;
/** 名称最大长度 */
const MAX_NAME_LENGTH = 31;

/**
 * Excel 风格的工作表标签页组件
 * 支持新增、删除、重命名、拖拽排序、滚动等功能
 */
const ExcelSheetTabs: React.FC<IExcelSheetTabsProps> = ({
  sheets,
  activeSheetId,
  onChange,
  onSheetsChange,
  className = '',
  contextMenuItems,
  readonly = false,
}) => {
  const { formatMessage } = useIntl();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  /** 原始名称（用于验证时排除自身） */
  const [originalName, setOriginalName] = useState('');
  /** 当前打开菜单的状态（坐标和目标 sheetId） */
  const [menuState, setMenuState] = useState<{ x: number; y: number; sheetId: string; } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  /** 强制更新 */
  const forceUpdate = useUpdate();
  /**
   * 标记编辑状态是否刚刚由当前事件触发启动。
   * 用于防止 useClickAway 在 startEditing 的同一事件周期内
   * 立即触发 finishEditing（React 事件处理与 document click listener 的时序冲突问题）
   */
  const editingJustStartedRef = useRef(false);

  // 删除确认 hook
  const [handleConfirmDelete] = useConfirmDelete();

  /** 是否只有一个工作表 */
  const isOnlyOneSheet = sheets.length <= 1;

  // 滑动效果
  const {
    containerRef,
    listContainerRef,
    isOverflow,
    slideDisabledState,
    handleSlideLeft,
    handleSlideRight,
  } = useSlideEffect({
    othersWidth: SCROLL_BUTTONS_WIDTH,
    updateDeps: [sheets],
  });

  // sheets 变化后延迟触发重新计算，确保 DOM 已更新
  useEffect(() => {
    const rafId = requestAnimationFrame(() => {
      forceUpdate();
    });
    return () => cancelAnimationFrame(rafId);
  }, [sheets, forceUpdate]);

  // 拖拽排序
  const {
    items: sortedSheets,
    setItems: setSortedSheets,
    onDragStart,
    onDragOver,
    onDrop,
    onDragEnd,
    draggingIndex,
  } = useDraggableList<ISheet>(sheets, (newSheets) => onSheetsChange?.(newSheets));


  // 同步外部 sheets 变化
  useEffect(() => {
    setSortedSheets(sheets);
  }, [sheets, setSortedSheets]);

  /**
   * 开始编辑工作表名称
   */
  const startEditing = useCallback((sheetId: string, currentName: string) => {
    setEditingId(sheetId);
    setEditingName(currentName);
    setOriginalName(currentName);
    setMenuState(null);
    // 标记编辑刚刚开始，防止同一事件中的 click-away 回调立即取消编辑
    editingJustStartedRef.current = true;
    setTimeout(() => {
      editingJustStartedRef.current = false;
    }, 0);
  }, []);

  /**
   * 完成编辑 - 带验证逻辑
   */
  const finishEditing = useCallback(() => {
    if (!editingId || !onSheetsChange) {
      setEditingId(null);
      setEditingName('');
      setOriginalName('');
      return;
    }

    const trimmedName = editingName.trim();

    // 如果名称没有变化，直接退出编辑
    if (trimmedName === originalName) {
      setEditingId(null);
      setEditingName('');
      setOriginalName('');
      return;
    }

    // 验证名称合法性
    const validation = validateSheetName(trimmedName);
    if (!validation.valid && validation.messageKey) {
      notification.error({ description: formatMessage({ id: validation.messageKey }) });
      // 恢复原名称
      setEditingName(originalName);
      setEditingId(null);
      setEditingName('');
      setOriginalName('');
      return;
    }

    // 获取其他工作表的名称（排除当前编辑的）
    const existingNames = sortedSheets.map(s => s.name);

    // 检查重名并生成唯一名称
    const uniqueName = getUniqueSheetName(trimmedName, existingNames, originalName);

    // 如果名称被修改了（重名处理），显示提示
    if (uniqueName !== trimmedName) {
      notification.warning({ description: formatMessage({ id: 'dba_smart_table_name_exists' }, { name: uniqueName }) });
    }

    // 更新工作表名称
    const newSheets = sortedSheets.map((sheet) =>
      sheet.id === editingId ? { ...sheet, name: uniqueName } : sheet
    );
    onSheetsChange(newSheets);

    // 确保清除 just-started 标记，允许后续正常触发 click-away
    editingJustStartedRef.current = false;
    setEditingId(null);
    setEditingName('');
    setOriginalName('');
  }, [editingId, editingName, originalName, sortedSheets, onSheetsChange]);

  /**
   * 处理重命名 - 点击重命名按钮打开输入框
   */
  const handleRename = useCallback((sheetId: string) => {
    const sheet = sheets.find((s) => s.id === sheetId);
    if (sheet) {
      // 先切换选中态到目标 sheet
      onChange?.(sheetId);
      // 然后打开编辑模式
      startEditing(sheetId, sheet.name);
    }
  }, [sheets, startEditing, onChange]);

  /**
   * 处理创建副本 - 添加到尾部，选中新建的 sheet
   * 通过 sourceId 字段标记副本来源，父组件在 onSheetsChange 中处理数据复制
   */
  const handleDuplicate = useCallback((sheetId: string) => {
    const sheet = sheets.find((s) => s.id === sheetId);
    if (sheet && onSheetsChange) {
      const existingNames = sheets.map(s => s.name);
      const newName = generateCopyName(sheet.name, existingNames);
      const newSheetId = `${Date.now()}`;
      // 新 sheet 带上 sourceId，父组件通过此字段识别副本并复制数据
      const newSheet: ISheet = { id: newSheetId, name: newName, sourceId: sheetId };
      onSheetsChange([...sheets, newSheet]);
      // 选中新建的 sheet
      onChange?.(newSheetId);
      notification.success({ description: formatMessage({ id: 'dba_smart_table_create_copy_success' }) });
    }
    // 菜单关闭由 renderMenuOverlay 统一处理
  }, [sheets, onSheetsChange, onChange, formatMessage]);

  /**
   * 处理删除 - 删除当前选中态时，默认往前一位
   */
  const handleDelete = useCallback(async (sheetId: string) => {
    if (isOnlyOneSheet) return;
    // 菜单关闭由 renderMenuOverlay 统一处理
    await handleConfirmDelete({
      mutate: async () => {
        if (onSheetsChange) {
          const deleteIndex = sheets.findIndex((s) => s.id === sheetId);
          const newSheets = sheets.filter((s) => s.id !== sheetId);
          onSheetsChange(newSheets);

          // 如果删除的是当前选中的 sheet，需要切换选中态
          if (sheetId === activeSheetId && onChange && newSheets.length > 0) {
            // 往前一位，如果是第一个则选中新的第一个
            const newActiveIndex = Math.max(0, deleteIndex - 1);
            onChange(newSheets[newActiveIndex].id);
          }
        }
      },
      message: formatMessage({ id: 'dba_smart_table_confirm_delete_sheet' }),
      caption: formatMessage({ id: 'dba_smart_table_delete_sheet_title' }),
    });
  }, [sheets, onSheetsChange, activeSheetId, onChange, isOnlyOneSheet, handleConfirmDelete, formatMessage]);

  /**
   * 新增工作表 - 选中新建的 sheet
   */
  const handleAddSheet = useCallback(() => {
    if (onSheetsChange) {
      const existingNames = sortedSheets.map(s => s.name);
      const blankSheetName = formatMessage({ id: 'dba_smart_table_blank_sheet' });
      const newName = generateNewSheetName(existingNames, blankSheetName);
      const newSheet: ISheet = { id: `${Date.now()}`, name: newName };
      onSheetsChange([...sortedSheets, newSheet]);
      // 选中新建的 sheet
      onChange?.(newSheet.id);
    }
  }, [sortedSheets, onSheetsChange, onChange, formatMessage]);

  /**
   * 点击工作表
   */
  const handleSheetClick = useCallback((sheetId: string) => {
    if (!editingId) onChange?.(sheetId);
  }, [editingId, onChange]);

  /**
   * 双击进入编辑（只读模式下禁用）
   */
  const handleDoubleClick = useCallback((sheetId: string, currentName: string) => {
    if (readonly) return;
    startEditing(sheetId, currentName);
  }, [startEditing, readonly]);

  /**
   * 输入框键盘事件
   */
  const handleInputKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      finishEditing();
    } else if (e.key === 'Escape') {
      setEditingId(null);
      setEditingName('');
      setOriginalName('');
    }
  }, [finishEditing]);

  // 点击外部关闭编辑
  // 注意：需要检查 editingJustStartedRef，防止 startEditing 与 click-away 在同一事件周期内冲突
  // React 在事件处理中同步重渲染后，input 出现在 DOM，随后 document click listener 触发，
  // 此时 editingId 已更新但用户尚未输入，会立即取消编辑
  useClickAway(() => {
    if (editingJustStartedRef.current) {
      return;
    }
    if (editingId) {
      finishEditing();
    }
  }, inputRef);

  // 自动聚焦输入框并定位光标到末尾
  useEffect(() => {
    if (editingId && inputRef.current) {
      inputRef.current.focus();
      const len = inputRef.current.value.length;
      inputRef.current.setSelectionRange(len, len);
    }
  }, [editingId]);

  // 点击外部或按 ESC 关闭菜单
  useEffect(() => {
    if (!menuState) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.dba-excel-sheet-menu-list')) {
        setMenuState(null);
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuState(null);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [menuState]);

  /**
   * 打开菜单
   */
  const openMenu = useCallback((sheetId: string, x: number, y: number) => {
    onChange?.(sheetId);
    setMenuState({ x, y, sheetId });
  }, [onChange]);

  /**
   * 默认右键菜单配置
   */
  const getDefaultMenuItems = useCallback((sheetId: string): IContextMenuItem[] => [
    { key: 'duplicate', label: formatMessage({ id: 'dba_smart_table_create_copy' }), iconSrc: iconCopy, onClick: () => handleDuplicate(sheetId) },
    { key: 'rename', label: formatMessage({ id: 'dba_smart_table_rename' }), iconSrc: iconEdit, onClick: () => handleRename(sheetId) },
    {
      key: 'delete',
      label: formatMessage({ id: 'dba_smart_table_delete' }),
      iconSrc: iconDelete,
      divider: true,
      disabled: isOnlyOneSheet,
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      onClick: () => handleDelete(sheetId),
    },
  ], [handleDuplicate, handleRename, handleDelete, isOnlyOneSheet, formatMessage]);

  /**
   * 渲染菜单内容
   */
  const renderMenuOverlay = (sheetId: string) => {
    const menuItems = contextMenuItems || getDefaultMenuItems(sheetId);

    return (
      <div className="dba-excel-sheet-menu-list">
        {menuItems.map((item) => (
          <React.Fragment key={item.key}>
            {item.divider && <div className="dba-excel-sheet-menu-list__divider" />}
            <div
              className={classNames('dba-excel-sheet-menu-list__item', {
                'dba-excel-sheet-menu-list__item--with-divider': item.divider,
                'dba-excel-sheet-menu-list__item--disabled': item.disabled,
              })}
              onClick={() => {
                if (item.disabled) return;
                item.onClick?.(sheetId);
                setMenuState(null);
              }}
            >
              {item.iconSrc && <img src={item.iconSrc} alt="" className="dba-excel-sheet-menu-list__icon" />}
              <span>{item.label}</span>
            </div>
          </React.Fragment>
        ))}
      </div>
    );
  };

  /**
   * 渲染工作表标签
   */
  const renderSheetTab = (sheet: ISheet, index: number) => {
    const isActive = activeSheetId === sheet.id;
    const isEditing = editingId === sheet.id;
    const isDragging = draggingIndex === index;

    return (
      <div
        key={sheet.id}
        draggable={!isEditing && !readonly}
        onDragStart={readonly ? undefined : () => onDragStart(index)}
        onDragOver={readonly ? undefined : (e) => onDragOver(e, index)}
        onDrop={readonly ? undefined : onDrop}
        onDragEnd={readonly ? undefined : onDragEnd}
        className={classNames('dba-excel-sheet-tab', {
          'dba-excel-sheet-tab--active': isActive,
          'dba-excel-sheet-tab--dragging': isDragging,
        })}
        onClick={() => handleSheetClick(sheet.id)}
        onDoubleClick={() => handleDoubleClick(sheet.id, sheet.name)}
        onContextMenu={(e) => {
          if (readonly) return;
          e.preventDefault();
          openMenu(sheet.id, e.clientX, e.clientY);
        }}
      >
        {isEditing ? (
          <input
            ref={inputRef}
            type="text"
            className="dba-excel-sheet-tab__input"
            value={editingName}
            onChange={(e) => setEditingName(e.target.value)}
            onKeyDown={handleInputKeyDown}
            onBlur={finishEditing}
            maxLength={MAX_NAME_LENGTH}
            style={{ width: `${Math.max(editingName.length * 14, 60)}px` }}
          />
        ) : (
          <>
            <EllipsisHover text={sheet.name} className="dba-excel-sheet-tab__name" />
            {!readonly && (
              <span
                className="dba-excel-sheet-tab__menu-trigger"
                onClick={(e) => {
                  e.stopPropagation();
                  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                  openMenu(sheet.id, rect.left, rect.bottom);
                }}
              >
                <img src={iconSheetMore} alt="more" />
              </span>
            )}
          </>
        )}
        {isActive && (
          <>
            <span className="dba-excel-sheet-tab__corner-left" />
            <span className="dba-excel-sheet-tab__corner-right" />
          </>
        )}
      </div>
    );
  };

  return (
    <div className={classNames('dba-excel-sheet-tabs', className)}>
      <div className="dba-excel-sheet-tabs__container">
        {/* 新增按钮 - 只读模式下不渲染 */}
        {!readonly && (
          <div className="dba-excel-sheet-tabs__add-box">
            <CommonBubble content={formatMessage({ id: 'dba_smart_table_new_blank_sheet' })} placement="bottom" overlayStyle={{ zIndex: 9999 }}>
              <button 
                className="dba-excel-sheet-tabs__add-btn" 
                onClick={handleAddSheet}
                aria-label={formatMessage({ id: 'dba_smart_table_new_blank_sheet' })}
                tabIndex={0}
              >
                {/* <PlusOutlined /> */}
                <img src={iconSheetAdd} alt="add" />
              </button>
            </CommonBubble>
            <span className="dba-excel-sheet-tabs__divider" />
          </div>
        )}

        {/* 标签列表 */}
        <div className="dba-excel-sheet-tabs__list-wrapper" ref={containerRef}>
          <div className="dba-excel-sheet-tabs__list" ref={listContainerRef}>
            {sortedSheets.map(renderSheetTab)}
            {isOverflow && <span className="dba-excel-sheet-tabs__buffer" />}
          </div>
        </div>

        {/* 滚动按钮 */}
        {isOverflow && (
          <div className="dba-excel-sheet-tabs__scroll-box">
            <Icon
              type="arrowleft"
              className={classNames('dba-excel-sheet-tabs__scroll-icon', {
                'dba-excel-sheet-tabs__scroll-icon--disabled': slideDisabledState.prev,
              })}
              onClick={slideDisabledState.prev ? undefined : handleSlideLeft}
            />
            <Icon
              type="arrowleft"
              className={classNames('dba-excel-sheet-tabs__scroll-icon', 'dba-excel-sheet-tabs__scroll-icon--right', {
                'dba-excel-sheet-tabs__scroll-icon--disabled': slideDisabledState.next,
              })}
              onClick={slideDisabledState.next ? undefined : handleSlideRight}
            />
          </div>
        )}
      </div>
      {menuState && !readonly && createPortal(
        <div style={{ position: 'fixed', left: menuState.x, top: menuState.y, zIndex: 9999 }}>
          {renderMenuOverlay(menuState.sheetId)}
        </div>,
        document.body
      )}
    </div>
  );
};

export default ExcelSheetTabs;
